import React, { createContext, ReactElement, useContext } from 'react';
import { Option } from 'fp-ts/lib/Option';
import { useCase, CriteriaCallback } from 'adapter/in/common/hooks/useCase';
import Dashboard from 'domain/entity/dashboard';
import { GetDashboards } from 'use-case/dashboards/get/getDashboardsUseCase';
import { CreateDashboard } from 'use-case/dashboard/create/createDashboardUseCase';
import BasicEntity from 'domain/entity/basicEntity';
import { ErrorType } from 'domain/types';

export type Dashboards = Array<Dashboard>;
export type GetDashboardsResult = Option<Dashboard[]>;

type DashboardsGet = {
    get: CriteriaCallback;
    getIsLoading: boolean;
    getResult: Option<Dashboards>;
    getError: ErrorType;
    getReset: () => void;
};

type DashboardCreate = {
    create: CriteriaCallback;
    createIsLoading: boolean;
    createResult: Option<BasicEntity>;
    createError: ErrorType;
    createReset?: () => void;
};

export type DashboardsType = DashboardsGet & DashboardCreate;

export const DashboardsContext = createContext({} as DashboardsType);
export const useDashboards = (): DashboardsType => useContext(DashboardsContext);

export type DashboardsProviderType = {
    getDashboards: GetDashboards;
    createDashboard: CreateDashboard;
    children: ReactElement[] | ReactElement;
};

export const DashboardsProvider = (props: DashboardsProviderType): ReactElement => {
    const { children, getDashboards, createDashboard } = props;

    const { isLoading: getIsLoading, data: getResult, error: getError, execute: get, reset } = useCase(getDashboards, false);
    const { isLoading: createIsLoading, data: createResult, error: createError, execute: create, reset: createReset } = useCase(
        createDashboard,
        false
    );

    return (
        <DashboardsContext.Provider
            value={{
                get,
                getIsLoading,
                getResult,
                getError,
                getReset: reset,

                create,
                createIsLoading,
                createResult,
                createError,
                createReset,
            }}
        >
            {children}
        </DashboardsContext.Provider>
    );
};
